import ContentShell from "components/ContentShell";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import TranslateIcon from "@mui/icons-material/Translate";
import CloseIcon from "@mui/icons-material/Close";
import configData from "config/config.json";
import { useSnackbar } from "hooks/useSnackbar";
import useBannerStyles from "styles/banner/bannerEditor";
import { SimpleTextInput } from "components/FormTextInput";
import { SimpleTextareaInput } from "components/FormTextArea";
import {
  createNotification,
  getNotificationInfo,
  updateNotification,
} from "services/notificationService";
import FilePicker from "components/FilePicker";
import {
  uploadImageV2,
  uploadContentTranslations,
} from "services/uploadService";
import { SimpleSelectInput } from "components/FormSelectInput";
import SearchableInput from "components/SearchableInput";
import BulkTranslationUpload from "components/BulkTranslationUpload";
import TranslationButtons from "components/TranslationButtons";
import useOpenClose from "hooks/useOpenClose";
import ViewLanguageModal from "screens/modals/ViewLanguageModal";
import SimpleSwitch from "components/Switch";
import sessionHelper from "services/sessionHelper";
import { ImageType, DEFAULT_IMAGE_DIMENSIONS } from "utils/imageConstants";
import ScheduleFields from "components/schedule/ScheduleFields";
import SectionCard from "components/SectionCard";
import {
  assertScheduleForSubmit,
  getScheduleLockState,
} from "utils/contentSchedule";
import { listPackLibraryImages } from "services/packServices";
import { notificationValidation } from "utils/validation";

interface NotificationDetails {
  _id?: string;
  title: string;
  titleTrans: Record<string, any>;
  body: string;
  bodyTrans: Record<string, any>;
  fileType: string;
  isActive: boolean;
  territory: string;
  operators: any[];
  imageUrl?: string;
  archivedAt?: string;
  startDate?: string | null;
  endDate?: string | null;
  imageRepositoryId?: string | null;
  removeImage?: boolean;
}

const _notificationDetails: NotificationDetails = {
  title: "",
  titleTrans: {},
  body: "",
  bodyTrans: {},
  fileType: "",
  isActive: false,
  territory: "all",
  operators: [],
  startDate: null,
  endDate: null,
  imageRepositoryId: null,
};

const NotificationEditor = () => {
  const bannerStyles = useBannerStyles();
  const location = useLocation();
  const { showSnackbar } = useSnackbar();
  const id = location.pathname.split("/").slice(-1)[0];
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [notificationDetails, setNotificationDetails] = useState(_notificationDetails);
  const [image, setImage] = useState(null);
  const [imageDimensions, setImageDimensions] = useState<{
    targetWidth: number;
    targetHeight: number;
    originalWidth: number;
    originalHeight: number;
  } | null>(null);
  const [translationFile, setTranslationFile] = useState<File | null>(null);
  const { isOpen, close, open } = useOpenClose();
  const [modalData, setModalData] = useState({
    language: "",
    inputValue: "",
    field: null,
    viewData: [],
  });
  const [libraryImages, setLibraryImages] = useState<any[]>([]);
  const initialSchedule = React.useRef<{
    startDate?: string | null;
    endDate?: string | null;
  }>({});
  const { isStatusScheduleControlled, isScheduleBlockedByStatus } = getScheduleLockState({
    isLive: notificationDetails.isActive,
    startDate: notificationDetails.startDate,
    endDate: notificationDetails.endDate,
  });
  const selectedLibraryImage = libraryImages.find(
    (img) => img.id === notificationDetails.imageRepositoryId,
  );
  const libraryPreviewUrl = selectedLibraryImage?.imageUrl;
  const uploadPreviewUrl = useMemo(
    () => (image ? URL.createObjectURL(image) : ""),
    [image],
  );
  const previewImageUrl =
    uploadPreviewUrl || libraryPreviewUrl || notificationDetails?.imageUrl;

  const cancel = () => navigate(configData.routes.notifications.notifications);

  // Allow-list of UpdateNotificationInput, so output-only fields from getNotificationInfo are never sent.
  const buildUpdatePayload = (): any => {
    const {
      _id,
      title,
      titleTrans,
      body,
      bodyTrans,
      territory,
      operators,
      startDate,
      endDate,
      fileType,
      isActive,
      imageRepositoryId,
      removeImage,
    } = notificationDetails;

    const payload: any = {
      _id,
      title,
      titleTrans,
      body,
      bodyTrans,
      territory,
      operators,
      startDate,
      endDate,
    };
    if (fileType !== "") payload.fileType = fileType;
    // While the schedule owns the status, keep the stored one so an edit can't pause or block it.
    if (!isStatusScheduleControlled) payload.isActive = isActive;
    // Direct-upload notifications always report imageRepositoryId as null, so send it only when set.
    if (imageRepositoryId) payload.imageRepositoryId = imageRepositoryId;
    // Explicit intent flag, sent only when Remove Image was actually clicked this session.
    if (removeImage) payload.removeImage = true;
    return payload;
  };

  const submit = async () => {
    try {
      setLoading(true);
      await notificationValidation.validate(notificationDetails);
      assertScheduleForSubmit(notificationDetails, initialSchedule.current);

      let response;
      if (id === "create") {
        // removeImage is an update-only flag; CreateNotificationInput rejects it.
        const { removeImage, ...createPayload } = notificationDetails;
        response = await createNotification(createPayload);

        if (response?.data?.fileUploadUrl) {
          await upload(response.data.fileUploadUrl);
        }

        if (translationFile && response?.data?._id) {
          await uploadContentTranslations(translationFile, "NOTIFICATION", response.data._id);
        }
      } else {
        response = await updateNotification(buildUpdatePayload());

        if (notificationDetails.fileType !== "" && response?.data?.fileUploadUrl) {
          await upload(response.data.fileUploadUrl);
        }
      }
      showSnackbar(response.message, "success");
      setLoading(false);
      navigate(configData.routes.notifications.notifications);
    } catch (error) {
      if (error?.inner) error.inner.forEach((_err) => showSnackbar(_err.message, "error"));
      showSnackbar(error.message, "error");
      setLoading(false);
    }
  };

  const upload = async (url: string) => {
    try {
      const defaultDims = DEFAULT_IMAGE_DIMENSIONS[ImageType.NOTIFICATION];
      const resp = await uploadImageV2({
        data: image,
        uploadUrl: url,
        resize: true,
        resizeToWidth: imageDimensions?.targetWidth || defaultDims.width,
        resizeToHeight: imageDimensions?.targetHeight || defaultDims.height,
      });

      if (resp.status === 200) showSnackbar(resp.message ?? "Image Uploaded", "success");
      return resp;
    } catch (error) {
      showSnackbar(error.message || "Error uploading image!");
    }
  };

  const onChange = (e: any) => {
    const { name, value } = e?.target;
    let data = {
      ...notificationDetails,
      [name]: name === "isActive" ? !notificationDetails.isActive : value,
    };
    if (["title", "body"].includes(name)) {
      data[`${name}Trans`]["en"] = value;
    }
    setNotificationDetails((state) => ({ ...state, ...data }));
  };

  const onSelectImage = (image: any, dimensions?: any) => {
    setImage(image);
    if (dimensions) {
      setImageDimensions(dimensions);
    }
    setNotificationDetails((state) => ({
      ...state,
      fileType: image ? "image/webp" : state.fileType,
      imageRepositoryId: image ? null : state.imageRepositoryId,
      removeImage: false,
    }));
  };

  const onSelectLibraryImage = (e: any) => {
    const { value } = e?.target;
    setImage(null);
    setNotificationDetails((state) => ({
      ...state,
      imageRepositoryId: value || null,
      fileType: "",
      removeImage: false,
    }));
  };

  // A notification may have no image; removeImage is the explicit signal, as imageRepositoryId can't be.
  const onRemoveImage = () => {
    setImage(null);
    setNotificationDetails((state) => ({
      ...state,
      imageRepositoryId: null,
      imageUrl: undefined,
      fileType: "",
      removeImage: true,
    }));
  };

  const fetchNotificationInfo = async () => {
    try {
      setLoading(true);
      const resp = await getNotificationInfo(id);
      if (resp?.data) {
        let titleTrans = { ...resp.data?.titleTrans };
        let bodyTrans = { ...resp.data?.bodyTrans };

        if (resp?.data?.title && !titleTrans["en"]) {
          titleTrans["en"] = resp?.data?.title;
        } else resp.data.title = titleTrans["en"];
        if (resp?.data?.body && !bodyTrans["en"]) {
          bodyTrans["en"] = resp?.data?.body;
        } else resp.data.body = bodyTrans["en"];

        initialSchedule.current = {
          startDate: resp.data?.startDate ?? null,
          endDate: resp.data?.endDate ?? null,
        };
        setNotificationDetails({
          ...resp.data,
          titleTrans,
          bodyTrans,
          operators: resp.data?.operators || [],
        });
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showSnackbar(error.message, "error");
    }
  };

  const getTranslatedData = (field: any) => {
    const _translatedData = Object.entries(notificationDetails?.[field]).map(([key, value]) => ({
      lang: key,
      inputValue: value,
    }));
    if (!_translatedData) return [];
    return _translatedData;
  };

  const handleOpenModal = (type, field) => {
    setModalData((state) => {
      if (type === "view") {
        let data = getTranslatedData(field);
        return { ...state, viewData: data, field, modal: type };
      } else {
        return {
          ...state,
          field,
          modal: type,
          language: "",
          inputValue: "",
        };
      }
    });
    open();
  };

  const handleChange = (event: any) => {
    const { name, value } = event.target;
    setModalData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLangEditClick = (data: any) => {
    close();
    setModalData((state) => ({
      ...state,
      language: data?.lang,
      inputValue: data?.inputValue,
      modal: "add",
    }));
    open();
  };

  const handleLangDeleteClick = (data: any) => {
    if (data.lang === "en") {
      showSnackbar("Cannot delete English translation, as it's a default languange", "error");
      return;
    }

    setModalData((state) => ({
      ...state,
      viewData: state.viewData.filter((item) => item.lang !== data.lang),
    }));

    setNotificationDetails((prev) => {
      prev[modalData.field] = Object.keys(prev[modalData.field]).reduce((acc, key) => {
        if (key !== data.lang) {
          acc[key] = prev[modalData.field][key];
        }
        return acc;
      }, {});
      return { ...prev };
    });
  };

  const handleAddTranslation = () => {
    const { field } = modalData;
    if (!field) {
      showSnackbar("Confused which field to add the translation", "error");
      return;
    }

    setNotificationDetails((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        [modalData.language]: modalData.inputValue,
      },
    }));

    if (modalData.language === "en" && modalData.inputValue !== "") {
      setNotificationDetails((details) => ({
        ...details,
        [field?.split("Trans")[0]]: modalData.inputValue,
        [field]: { ...details[field], en: modalData.inputValue },
      }));
    }

    setModalData((prev) => ({
      ...prev,
      inputValue: "",
      language: "",
      field: null,
    }));
    close();
  };

  const handleOperatorChange = (e: any) => {
    const { value } = e.target;
    if (Array.isArray(value)) {
      const operatorIds = value.map((item) => (typeof item === "object" ? item.id : item));
      setNotificationDetails((prev) => ({
        ...prev,
        operators: operatorIds,
      }));
    } else {
      setNotificationDetails((prev) => ({
        ...prev,
        operators: value ? [value] : [],
      }));
    }
  };

  useEffect(
    () => () => {
      if (uploadPreviewUrl) URL.revokeObjectURL(uploadPreviewUrl);
    },
    [uploadPreviewUrl],
  );

  useEffect(() => {
    if (id !== "create") fetchNotificationInfo();
    listPackLibraryImages()
      .then((resp) =>
        setLibraryImages(
          (resp?.data ?? []).map((img: any) => ({
            ...img,
            id: img?._id,
            label: img?.imageId,
          })),
        ),
      )
      .catch((error) => showSnackbar(error.message, "error"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fieldGrid = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: 2.5,
    alignItems: "start",
  };

  return (
    <ContentShell
      showBackButton
      onBackButtonClick={cancel}
      title={`${id === "create" ? "Create" : "Edit"} Notification`}
      loading={loading}
      actions={
        <Box className={bannerStyles.buttonContainer}>
          <Button variant="outlined" color="inherit" onClick={cancel}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" onClick={submit}>
            Save
          </Button>
        </Box>
      }
    >
      <Box sx={{ maxWidth: 980, mx: "auto", pb: 4 }}>
        <SectionCard
          step={1}
          icon={<ArticleOutlinedIcon fontSize="small" />}
          title="Notification Content"
          subtitle="What the notification says"
          accent="#1565c0"
          isComplete={!!notificationDetails.title && !!notificationDetails.body}
        >
          <Box sx={fieldGrid}>
            <SimpleTextInput
              label="Title"
              name="title"
              value={notificationDetails.title}
              onChange={onChange}
              button={<TranslationButtons field={"titleTrans"} onClick={handleOpenModal} />}
            />
            <SimpleTextareaInput
              label="Body"
              name="body"
              value={notificationDetails.body}
              onChange={onChange}
              buttons={<TranslationButtons field={"bodyTrans"} onClick={handleOpenModal} />}
            />
          </Box>
        </SectionCard>

        <SectionCard
          step={2}
          icon={<ImageOutlinedIcon fontSize="small" />}
          title="Notification Image"
          subtitle="Optional — pick an image from the Image Repository, or upload your own"
          accent="#00838f"
        >
          <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", alignItems: "flex-start" }}>
            <Box sx={{ flex: 1, minWidth: 260, display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Choose one image source. A library image is shared, so it can back several
                notifications and packs.
              </Typography>
              <SimpleSelectInput
                label="Library Image"
                name="imageRepositoryId"
                value={selectedLibraryImage ? notificationDetails.imageRepositoryId : ""}
                options={libraryImages}
                onChange={onSelectLibraryImage}
              />
              <FilePicker
                label="Upload Custom Image"
                value={image}
                name="notificationImage"
                showPreview={false}
                onFileSelect={onSelectImage}
                imageType={ImageType.NOTIFICATION}
                enableDimensionControl={true}
                metaData={{
                  note: `Recommended: ${DEFAULT_IMAGE_DIMENSIONS[ImageType.NOTIFICATION].width} x ${
                    DEFAULT_IMAGE_DIMENSIONS[ImageType.NOTIFICATION].height
                  } pixels. Images will be resized and converted to WebP.`,
                }}
              />
              {previewImageUrl && (
                <Button
                  size="small"
                  color="error"
                  startIcon={<CloseIcon fontSize="small" />}
                  onClick={onRemoveImage}
                  sx={{ alignSelf: "flex-start" }}
                >
                  Remove Image
                </Button>
              )}
            </Box>
            {previewImageUrl && (
              <Box
                component="img"
                src={previewImageUrl}
                alt="Image Preview"
                sx={{
                  aspectRatio: "16/9",
                  maxHeight: 200,
                  maxWidth: 400,
                  objectFit: "contain",
                }}
              />
            )}
          </Box>
        </SectionCard>

        <SectionCard
          step={3}
          icon={<PublicOutlinedIcon fontSize="small" />}
          title="Audience"
          subtitle="Which territory and operators see this notification"
          accent="#2e7d32"
        >
          <Box sx={fieldGrid}>
            <SimpleSelectInput
              label="Territory"
              name="territory"
              value={notificationDetails?.territory || ""}
              options={sessionHelper.countryOptions}
              onChange={onChange}
            />
            <SearchableInput
              selected={(notificationDetails.operators || []).map((operatorId) => {
                const operator = sessionHelper.operatorOptions?.find((op) => op.id === operatorId);
                return {
                  id: operatorId,
                  label: operator?.label || operatorId,
                };
              })}
              isMultiSelect={true}
              enableSelectAll={true}
              label={"Operators (Optional)"}
              name={"operators"}
              options={sessionHelper.operatorOptions || []}
              onOptionSelect={handleOperatorChange}
              placeholder="Select operators (optional)"
              loading={loading}
            />
          </Box>
        </SectionCard>

        <SectionCard
          step={4}
          icon={<CalendarMonthOutlinedIcon fontSize="small" />}
          title="Status & Schedule"
          subtitle="Manage the status by hand, or set an automatic window in UTC"
          accent="#6a1b9a"
        >
          <Box sx={{ mb: 3 }}>
            <SimpleSwitch
              legendText={"Notification Status"}
              name="isActive"
              value={notificationDetails.isActive}
              label={notificationDetails.isActive ? "Active" : "Inactive"}
              onChange={onChange}
              disabled={isStatusScheduleControlled}
              helperText={
                isStatusScheduleControlled
                  ? "Set by the schedule. Clear the dates to control the status here."
                  : undefined
              }
            />
          </Box>
          <ScheduleFields
            itemLabel="notification"
            startDate={notificationDetails.startDate}
            endDate={notificationDetails.endDate}
            savedStartDate={initialSchedule.current.startDate}
            savedEndDate={initialSchedule.current.endDate}
            disabled={isScheduleBlockedByStatus}
            onChange={(field, value) =>
              setNotificationDetails((state) => ({ ...state, [field]: value }))
            }
          />
        </SectionCard>

        <SectionCard
          step={5}
          icon={<TranslateIcon fontSize="small" />}
          title="Translations"
          subtitle="Upload title and body translations in bulk"
          accent="#5d4037"
        >
          <BulkTranslationUpload
            assetType="NOTIFICATION"
            assetId={id !== "create" ? id : undefined}
            fields={[
              { label: "Title", fieldName: "titleTrans" },
              { label: "Body", fieldName: "bodyTrans" },
            ]}
            {...(id === "create"
              ? { deferUpload: true, onFileChange: setTranslationFile }
              : { onSuccess: fetchNotificationInfo })}
          />
        </SectionCard>
      </Box>
      <ViewLanguageModal
        isMultiline={true}
        label="Select Language"
        isOpen={isOpen}
        close={close}
        onChange={handleChange}
        handleOnSubmit={handleAddTranslation}
        actionHandlers={{
          handleLangEditClick: handleLangEditClick,
          handleLangDeleteClick: handleLangDeleteClick,
        }}
        modalData={modalData}
        tableColumns={[
          { id: "lang", label: "Language", alignHead: "center", alignBody: "center" },
          { id: "inputValue", label: "Translation", alignBody: "center", alignHead: "center" },
          { id: "actions", label: "Actions", alignHead: "center", alignBody: "center" },
        ]}
      />
    </ContentShell>
  );
};

export default NotificationEditor;
